import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Shield, 
  Key, 
  Mail, 
  Lock, 
  Settings,
  Globe,
  ArrowLeft
} from 'lucide-react'

export function AuthenticationFeatureDetailPage() {
  const { t } = useTranslation('workforce')
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      <div className="container mx-auto px-4 py-16">
        {/* Back Button */}
        <div className="mb-6">
          <a 
            href="/workforce" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl mb-6">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('features.authentication.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('features.authentication.description')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('authenticationDetail.loginOptions')}</h3>
              
              <div className="space-y-4">
                <button className="w-full bg-white border-2 border-gray-300 px-4 py-3 rounded-lg text-sm flex items-center justify-center gap-3 hover:border-gray-400 transition-colors">
                  <div className="w-5 h-5 bg-gradient-to-r from-blue-500 to-blue-600 rounded"></div>
                  {t('authenticationDetail.continueWithGoogle')}
                </button>
                
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="text-sm text-gray-500">{t('authenticationDetail.or')}</span>
                  <div className="flex-1 h-px bg-gray-300"></div>
                </div>
                
                <div className="space-y-3">
                  <input 
                    className="w-full border border-gray-300 px-4 py-3 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                    placeholder={t('authenticationDetail.emailPlaceholder')} 
                  />
                  <input 
                    className="w-full border border-gray-300 px-4 py-3 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                    placeholder={t('authenticationDetail.passwordPlaceholder')}
                    type="password"
                  />
                </div>
                
                <button className="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
                  {t('authenticationDetail.login')}
                </button>
                
                <div className="text-center">
                  <a href="#" className="text-sm text-indigo-600 hover:text-indigo-700">
                    {t('authenticationDetail.forgotPassword')}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 text-indigo-600" />
                <h4 className="font-semibold text-gray-900">{t('authenticationDetail.features.oauth.title')}</h4>
              </div>
              <p className="text-gray-600 text-sm">
                {t('authenticationDetail.features.oauth.description')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Key className="w-5 h-5 text-indigo-600" />
                <h4 className="font-semibold text-gray-900">{t('authenticationDetail.features.local.title')}</h4>
              </div>
              <p className="text-gray-600 text-sm">
                {t('authenticationDetail.features.local.description')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-5 h-5 text-indigo-600" />
                <h4 className="font-semibold text-gray-900">{t('authenticationDetail.features.recovery.title')}</h4>
              </div>
              <p className="text-gray-600 text-sm">
                {t('authenticationDetail.features.recovery.description')}
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 mb-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('authenticationDetail.implementation.title')}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('authenticationDetail.implementation.jwt.title')}</h4>
              <p className="text-sm text-gray-600">
                {t('authenticationDetail.implementation.jwt.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Settings className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('authenticationDetail.implementation.spring.title')}</h4>
              <p className="text-sm text-gray-600">
                {t('authenticationDetail.implementation.spring.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Globe className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('authenticationDetail.implementation.oauth.title')}</h4>
              <p className="text-sm text-gray-600">
                {t('authenticationDetail.implementation.oauth.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Lock className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('authenticationDetail.implementation.hash.title')}</h4>
              <p className="text-sm text-gray-600">
                {t('authenticationDetail.implementation.hash.description')}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">{t('authenticationDetail.resetFlow.title')}</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <div className="font-medium text-gray-900">{t('authenticationDetail.resetFlow.steps.0.title')}</div>
                  <div className="text-sm text-gray-600">{t('authenticationDetail.resetFlow.steps.0.description')}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <div className="font-medium text-gray-900">{t('authenticationDetail.resetFlow.steps.1.title')}</div>
                  <div className="text-sm text-gray-600">{t('authenticationDetail.resetFlow.steps.1.description')}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <div className="font-medium text-gray-900">{t('authenticationDetail.resetFlow.steps.2.title')}</div>
                  <div className="text-sm text-gray-600">{t('authenticationDetail.resetFlow.steps.2.description')}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">{t('authenticationDetail.specifications.title')}</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-700">{t('authenticationDetail.specifications.backend')}</span>
                <span className="text-sm text-gray-900">{t('authenticationDetail.specifications.values.backend')}</span>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-700">{t('authenticationDetail.specifications.tokenType')}</span>
                <span className="text-sm text-gray-900">{t('authenticationDetail.specifications.values.tokenType')}</span>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-700">{t('authenticationDetail.specifications.passwordSecurity')}</span>
                <span className="text-sm text-gray-900">{t('authenticationDetail.specifications.values.passwordSecurity')}</span>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-700">{t('authenticationDetail.specifications.oauthProvider')}</span>
                <span className="text-sm text-gray-900">{t('authenticationDetail.specifications.values.oauthProvider')}</span>
              </div>
              
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-gray-700">{t('authenticationDetail.specifications.resetTtl')}</span>
                <span className="text-sm text-gray-900">{t('authenticationDetail.specifications.values.resetTtl')}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}